import "./media-carousel.scss";
import cn from "clsx";
import { useId, useRef, useState } from "react";
import {
  Splide,
  SplideSlide,
  SplideTrack,
  type Splide as SplideComponent,
} from "@splidejs/react-splide";
import "@splidejs/react-splide/css/core";
import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";
import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";
import { ArrowButton } from "design-system/components/primitives/arrow-button/arrow-button";
import { useBreakpoint } from "design-system/hooks/use-breakpoint";
import {
  CtaLinkProps,
  CtaLink,
} from "design-system/components/primitives/cta-link/cta-link";
import { Alignment } from "design-system/types/types";

export interface MediaCarouselItemProps {
  media: MediaAssetProps;
  ctaLink?: CtaLinkProps;
}

export interface MediaCarouselProps {
  headerContent?: ComponentHeaderProps;
  HeadingLevel?: "h2" | "h3";
  /** Global alignment class */
  align?: Alignment;
  items: Array<MediaCarouselItemProps>;
}

/**
 * This component is for displaying media assets in a carousel.
 *
 * ## How it works
 *
 * On desktop, previous, current, and next slides are visible.
 *
 * On mobile, dragging is enabled for better UX.
 *
 * ## See it in use on...
 * - A [landing page](/story/example-pages-landing-pages-card-topper--story)
 *
 *
 * - **`id: CB-010-000-00`**
 * - **`data-region: cb__media-carousel`**
 */
export function MediaCarousel({
  headerContent,
  items,
  align = "full",
  HeadingLevel = "h2",
}: MediaCarouselProps) {
  const wrapperClass = cn(`hbs-global-align-${align}`);
  const { isMobile } = useBreakpoint();
  const [currentSlide, setCurrentSlide] = useState(0);

  const titleId = useId();

  const leftCarouselRef = useRef<SplideComponent | null>(null);
  const mainCarouselRef = useRef<SplideComponent | null>(null);
  const rightCarouselRef = useRef<SplideComponent | null>(null);

  const prevButton = (
    <ArrowButton
      variant="prev"
      onClick={() => {
        if (mainCarouselRef.current) {
          mainCarouselRef.current.go("<");
        }
      }}
      data-testid="media-carousel-button-left"
      className="hbs-media-carousel__button--prev"
    />
  );
  const nextButton = (
    <ArrowButton
      variant="next"
      onClick={() => {
        if (mainCarouselRef.current) {
          mainCarouselRef.current.go(">");
        }
      }}
      data-testid="media-carousel-button-left"
      className="hbs-media-carousel__button--next"
    />
  );

  // [1, 2, 3, 4, 5] -> [5, 1, 2, 3, 4]
  const itemsShiftedBackwards = items.length
    ? ([
        items[items.length - 1],
        ...items.slice(0, -1),
      ] as Array<MediaCarouselItemProps>)
    : [];

  // [1, 2, 3, 4, 5] -> [2, 3, 4, 5, 1]
  const itemsShiftedForwards = items.length
    ? ([...items.slice(1), items[0]] as Array<MediaCarouselItemProps>)
    : [];

  const carouselRef = useRef<HTMLDivElement>(null);

  // Generate unique IDs for each slide to prevent conflicts with cloned slides
  const getUniqueId = (index: number, prefix: string) =>
    `${prefix}-${index}-${currentSlide}`;

  const handleMove = (newIndex: number) => {
    const carouselElement = carouselRef.current;
    if (!carouselElement) return;

    // Find all slides
    const slides = carouselElement.querySelectorAll(
      ".hbs-media-carousel__item",
    );

    // Only destroy players in non-active slides
    slides.forEach((slide, index) => {
      if (index !== newIndex) {
        const players = slide.querySelectorAll('[id^="kaltura-player-"]');
        players.forEach((player) => {
          const resetEvent = new CustomEvent("resetKalturaPlayer");
          player.dispatchEvent(resetEvent);

          const playerId = player.id;
          if (window.kWidget) {
            window.kWidget.destroy(playerId);
          }
        });
      }
    });

    // Update current slide to force re-render of all carousels
    setCurrentSlide(newIndex);

    // Sync other carousels
    if (leftCarouselRef.current) {
      leftCarouselRef.current.go(newIndex);
    }
    if (rightCarouselRef.current) {
      rightCarouselRef.current.go(newIndex);
    }
  };

  return (
    <div className={wrapperClass} data-region="cb__media-carousel">
      <ComponentHeader
        {...(headerContent ? { ...headerContent, titleId } : headerContent)}
        HeadingLevel={HeadingLevel}
      />

      <div className="hbs-media-carousel" ref={carouselRef}>
        <div className="hbs-media-carousel__container hbs-media-carousel__container--previous">
          <div aria-hidden>
            <Splide
              options={{
                type: "loop",
                drag: false,
                arrows: false,
                pagination: false,
                paginationKeyboard: false,
                speed: 600,
              }}
              hasTrack={false}
              ref={leftCarouselRef}
            >
              <SplideTrack>
                {itemsShiftedBackwards.map((item, i) => (
                  <SplideSlide
                    className="hbs-media-carousel__item"
                    key={getUniqueId(i, "prev")}
                  >
                    <MediaAsset {...item.media} />
                  </SplideSlide>
                ))}
              </SplideTrack>
            </Splide>
          </div>
          {/* This prev button only displayed on larger viewports */}
          <div className="hbs-media-carousel__controls">{prevButton}</div>
        </div>
        <div className="hbs-media-carousel__container hbs-media-carousel__container--current">
          <Splide
            {...(headerContent
              ? { "aria-labelledby": titleId }
              : { "aria-label": "Media Carousel" })}
            options={{
              type: "loop",
              drag: isMobile,
              arrows: false,
              pagination: false,
              speed: 600,
              // Add these options to prevent slide cloning issues
              clones: 0,
              rewind: true,
              rewindByDrag: true,
            }}
            hasTrack={false}
            ref={mainCarouselRef}
            onMove={(splide, index, prev, dest) => {
              handleMove(dest);
            }}
          >
            <SplideTrack>
              {items.map((item, i) => (
                <SplideSlide
                  className="hbs-media-carousel__item"
                  key={getUniqueId(i, "slide")}
                >
                  <MediaAsset {...item.media} key={getUniqueId(i, "media")} />
                  {item.ctaLink && <CtaLink {...item.ctaLink} isSmall />}
                </SplideSlide>
              ))}
            </SplideTrack>
          </Splide>
          <div className="hbs-media-carousel__controls">
            {prevButton}
            {nextButton}
          </div>
        </div>
        <div className="hbs-media-carousel__container hbs-media-carousel__container--next">
          <div aria-hidden>
            <Splide
              options={{
                type: "loop",
                drag: false,
                arrows: false,
                pagination: false,
                paginationKeyboard: false,
                speed: 600,
              }}
              hasTrack={false}
              ref={rightCarouselRef}
            >
              <SplideTrack>
                {itemsShiftedForwards.map((item, i) => (
                  <SplideSlide
                    className="hbs-media-carousel__item"
                    key={getUniqueId(i, "next")}
                  >
                    <MediaAsset {...item.media} />
                  </SplideSlide>
                ))}
              </SplideTrack>
            </Splide>
          </div>
          <div className="hbs-media-carousel__controls">
            {/* This prev button only displayed on smaller viewports */}
            {prevButton}
            {nextButton}
          </div>
        </div>
      </div>
    </div>
  );
}
